import React, { useCallback, useEffect, useState } from 'react';
import { styled, useTheme } from '@mui/material/styles';
import Title from '../../components/Title';
import { useDispatch, useSelector } from 'react-redux';
import {
  getNewsOnceAsync,
  selectNews,
  updateNewsAsync,
  createNewsAsync
} from '../../redux/slices/newsSlice';
import {
  Box,
  Button,
  Collapse,
  IconButton,
  Paper,
  Tooltip,
  Typography
} from '@mui/material';
import {
  Delete,
  ExpandMore,
  ExpandLess,
  ArrowUpward,
  ArrowDownward
} from '@mui/icons-material';
import Input from '../../components/Input';
import { NewsState } from './interface';
import { NewsItem } from '../../resources/types/newsTypes';
import deepCopy from 'deep-copy';

const PREFIX = 'NewsPage';

const classes = {
  newsBlock: `${PREFIX}-newsBlock`,
  newsHeader: `${PREFIX}-newsHeader`
};

const Root = styled('div')(({ theme }) => ({
  [`& .${classes.newsBlock}`]: {
    marginBottom: '16px',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: '8px',
    overflow: 'hidden'
  },

  [`& .${classes.newsHeader}`]: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 16px',
    cursor: 'pointer',
    backgroundColor: theme.palette.mode === 'light' ? '#FAFAFA' : '#2A2A2A',
    '&:hover': {
      backgroundColor: theme.palette.mode === 'light' ? '#F5F5F5' : '#333333'
    }
  }
}));

const NewsPage = () => {
  const theme = useTheme();
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(getNewsOnceAsync());
  }, [dispatch]);

  const news = useSelector(selectNews);

  const [currentNews, setCurrentNews] = useState<NewsState>({
    items: []
  });

  const [expandedItems, setExpandedItems] = useState<{[key: number]: boolean}>({});

  useEffect(() => {
    if (news.once) {
      setCurrentNews({
        ...news.once,
        items: [...news.once.items, { context: '', title: '' }]
      } as NewsState);
      setExpandedItems({});
    } else if (news.once === null) {
      setCurrentNews({ items: [{ title: '', context: '' }] });
    }
  }, [news.once]);

  const toggleExpand = (index: number) => {
    setExpandedItems(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const createChangeNewsItemHandler = useCallback(
    (property: keyof NewsItem, index: number) => (event: any) => {
      event.persist();
      setCurrentNews(prevState => {
        const nextState = deepCopy(prevState);

        nextState.items[index][property] = event.target.value;

        if (index === nextState.items.length - 1 && (nextState.items[index].title || nextState.items[index].context)) {
          nextState.items.push({ title: '', context: '' });
        }

        return nextState;
      });
    },
    []
  );

  const createDeleteNewsItemHandler = useCallback(
    (index: number) => async () => {
      // Фільтруємо елемент який видаляємо
      const updatedItems = currentNews.items.filter((_, i) => i !== index);
      const filteredItems = updatedItems.filter(item => item.title || item.context);
      
      // Одразу оновлюємо локальний стан
      setCurrentNews({
        ...currentNews,
        items: [...filteredItems, { title: '', context: '' }]
      });
      
      // Скидаємо expanded стан
      setExpandedItems({});
      
      // Зберігаємо на сервер
      await dispatch(
        updateNewsAsync({
          ...currentNews,
          items: filteredItems
        })
      );
      
      // Перезавантажуємо дані з сервера для синхронізації
      dispatch(getNewsOnceAsync());
    },
    [currentNews, dispatch]
  );

  const createSaveNewsItemHandler = useCallback(
    (index: number) => async () => {
      const filteredItems = currentNews.items.filter(item => item.title || item.context);
      
      setCurrentNews({
        ...currentNews,
        items: [...filteredItems, { title: '', context: '' }]
      });
      
      setExpandedItems(prev => ({
        ...prev,
        [index]: false
      }));
      
      if (news.once) {
        await dispatch(
          updateNewsAsync({
            ...currentNews,
            items: filteredItems
          })
        );
      } else {
        await dispatch(
          createNewsAsync({ items: filteredItems }, () => {})
        );
      }
      
      dispatch(getNewsOnceAsync());
    },
    [currentNews, dispatch, news.once]
  );

  const moveNewsUp = useCallback(
    (index: number) => async () => {
      if (index === 0) return; // Вже найвище
      
      const newItems = [...currentNews.items];
      // Міняємо місцями з попереднім елементом
      [newItems[index - 1], newItems[index]] = [newItems[index], newItems[index - 1]];
      
      // Фільтруємо порожні елементи
      const filteredItems = newItems.filter(item => item.title || item.context);
      
      // Оновлюємо локальний стан
      setCurrentNews({
        ...currentNews,
        items: [...filteredItems, { title: '', context: '' }]
      });
      
      // Оновлюємо expanded стан (переносимо розгорнутість на нову позицію)
      setExpandedItems(prev => {
        const newExpanded = { ...prev };
        const currentExpanded = prev[index];
        const prevExpanded = prev[index - 1];
        newExpanded[index] = prevExpanded || false;
        newExpanded[index - 1] = currentExpanded || false;
        return newExpanded;
      });
      
      // Зберігаємо на сервер
      await dispatch(
        updateNewsAsync({
          ...currentNews,
          items: filteredItems
        })
      );
      
      // Перезавантажуємо дані з сервера для синхронізації
      dispatch(getNewsOnceAsync());
    },
    [currentNews, dispatch]
  );

  const moveNewsDown = useCallback(
    (index: number) => async () => {
      // Перевіряємо чи це не останній непорожній елемент
      const nonEmptyItems = currentNews.items.filter(item => item.title || item.context);
      const currentItemIndexInNonEmpty = nonEmptyItems.findIndex(
        item => item.title === currentNews.items[index].title && item.context === currentNews.items[index].context
      );
      
      if (currentItemIndexInNonEmpty === nonEmptyItems.length - 1) return; // Вже найнижче
      
      const newItems = [...currentNews.items];
      // Міняємо місцями з наступним елементом
      [newItems[index], newItems[index + 1]] = [newItems[index + 1], newItems[index]];
      
      // Фільтруємо порожні елементи
      const filteredItems = newItems.filter(item => item.title || item.context);
      
      // Оновлюємо локальний стан
      setCurrentNews({
        ...currentNews,
        items: [...filteredItems, { title: '', context: '' }]
      });
      
      // Оновлюємо expanded стан (переносимо розгорнутість на нову позицію)
      setExpandedItems(prev => {
        const newExpanded = { ...prev };
        const currentExpanded = prev[index];
        const nextExpanded = prev[index + 1];
        newExpanded[index] = nextExpanded || false;
        newExpanded[index + 1] = currentExpanded || false;
        return newExpanded;
      });
      
      // Зберігаємо на сервер
      await dispatch(
        updateNewsAsync({
          ...currentNews,
          items: filteredItems
        })
      );
      
      // Перезавантажуємо дані з сервера для синхронізації
      dispatch(getNewsOnceAsync());
    },
    [currentNews, dispatch]
  );

  return (
    <Root>
      <Paper sx={{ border: theme.palette.mode === 'light' ? '2px solid #FFFFFF' : 'none', borderRadius: 2, padding: '20px' }}>
        <Title style={{ marginBottom: 20 }}>Новини</Title>
        
        <Box>
          {currentNews.items.map((item, index) => {
            const isExpanded = expandedItems[index] || false;
            const isEmpty = !item.title && !item.context;
            
            return (
              <Box key={`news-${index}-${item.title}`} className={classes.newsBlock}>
                {/* Заголовок блоку */}
                <Box className={classes.newsHeader}>
                  <Box 
                    sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}
                    onClick={() => toggleExpand(index)}
                  >
                    {isExpanded ? <ExpandLess /> : <ExpandMore />}
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {item.title || 'Новий блок'}
                    </Typography>
                  </Box>
                  
                  {/* Кнопки управління */}
                  {!isEmpty && (
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {/* Кнопка вгору */}
                      {index > 0 && (
                        <Tooltip title='Перемістити вгору'>
                          <IconButton
                            onClick={(e) => {
                              e.stopPropagation();
                              moveNewsUp(index)();
                            }}
                            size='medium'
                            sx={{
                              bgcolor: theme.palette.mode === 'light' ? '#F5F5F5' : '#424242',
                              color: theme.palette.mode === 'light' ? '#616161' : '#FFF',
                              borderRadius: '8px',
                              '&:hover': { 
                                bgcolor: theme.palette.mode === 'light' ? '#E0E0E0' : '#3A3A3A' 
                              }
                            }}
                          >
                            <ArrowUpward fontSize='small' />
                          </IconButton>
                        </Tooltip>
                      )}
                      
                      {/* Кнопка вниз */}
                      {index < currentNews.items.length - 2 && (
                        <Tooltip title='Перемістити вниз'>
                          <IconButton
                            onClick={(e) => {
                              e.stopPropagation();
                              moveNewsDown(index)();
                            }}
                            size='medium'
                            sx={{
                              bgcolor: theme.palette.mode === 'light' ? '#F5F5F5' : '#424242',
                              color: theme.palette.mode === 'light' ? '#616161' : '#FFF',
                              borderRadius: '8px',
                              '&:hover': { 
                                bgcolor: theme.palette.mode === 'light' ? '#E0E0E0' : '#3A3A3A' 
                              }
                            }}
                          >
                            <ArrowDownward fontSize='small' />
                          </IconButton>
                        </Tooltip>
                      )}
                      
                      {/* Кнопка видалення */}
                      {currentNews.items.length > 1 && (
                        <Tooltip title='Видалити'>
                          <IconButton
                            onClick={(e) => {
                              e.stopPropagation();
                              createDeleteNewsItemHandler(index)();
                            }}
                            size='medium'
                            sx={{
                              bgcolor: theme.palette.mode === 'light' ? '#F5F5F5' : '#424242',
                              color: theme.palette.mode === 'light' ? '#616161' : '#FFF',
                              borderRadius: '8px',
                              '&:hover': { 
                                bgcolor: theme.palette.mode === 'light' ? '#E0E0E0' : '#D32F2F' 
                              }
                            }}
                          >
                            <Delete fontSize='small' />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  )}
                </Box>

                {/* Розкритий контент */}
                <Collapse in={isExpanded}>
                  <Box sx={{ padding: '16px' }}>
                    <Input
                      InputLabelProps={{ shrink: true }}
                      label='Заголовок'
                      style={{ marginBottom: 16 }}
                      value={item.title}
                      onChange={createChangeNewsItemHandler('title', index)}
                    />
                    <Input
                      InputLabelProps={{ shrink: true }}
                      label='Контент'
                      multiline
                      fullWidth
                      rows={6}
                      value={item.context}
                      onChange={createChangeNewsItemHandler('context', index)}
                      style={{ marginBottom: 16 }}
                    />
                    
                    {/* Кнопка Зберегти */}
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={createSaveNewsItemHandler(index)}
                    >
                      Зберегти
                    </Button>
                  </Box>
                </Collapse>
              </Box>
            );
          })}
        </Box>
      </Paper>
    </Root>
  );
};

export default NewsPage;
